import type { CSSProperties } from 'react'
import { COLORS, FONT, SPACING, RADIUS } from '../ui/tokens'
import type { Ride } from '../../types/ride'

interface Props { ride: Ride; onRequest?: () => void; onCancel?: () => void; showCancel?: boolean }

export function RideCard({ ride, onRequest, onCancel, showCancel }: Props) {
  const depTime = new Date(ride.departsAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  const depDate = new Date(ride.departsAt).toLocaleDateString('ru-RU', { weekday: 'short', day: '2-digit', month: 'short' })
  const isToday = new Date(ride.departsAt).toDateString() === new Date().toDateString()

  const dotStyle = (color: string): CSSProperties => ({
    position: 'absolute', left: 0, width: 10, height: 10, borderRadius: '50%',
    backgroundColor: color, border: `2px solid ${COLORS.bgCard}`,
  })

  return (
    <div style={{ backgroundColor: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: SPACING.md, marginBottom: SPACING.sm }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: SPACING.sm, position: 'relative', paddingLeft: 20 }}>
        <div style={{ position: 'absolute', left: 4, top: 14, bottom: 14, width: 2, backgroundColor: COLORS.border }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <span style={{ ...dotStyle(COLORS.success), top: 3 }} />
          <span style={{ fontSize: FONT.base, color: COLORS.textPrimary, fontWeight: 600 }}>{ride.originName}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm }}>
          <span style={{ ...dotStyle(COLORS.error), bottom: 3 }} />
          <span style={{ fontSize: FONT.base, color: COLORS.textPrimary }}>{ride.destName}</span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: onRequest || showCancel ? SPACING.sm : 0 }}>
        <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center' }}>
          <span style={{ fontSize: FONT.xs, color: COLORS.textSecond }}>👤 {ride.driverName}</span>
          <span style={{ color: COLORS.border }}>·</span>
          <span style={{ fontSize: FONT.xs, color: COLORS.textSecond }}>💺 {ride.seatsFree}/{ride.seatsTotal}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: FONT.sm, color: COLORS.accent, fontWeight: 700 }}>{depTime}</div>
          <div style={{ fontSize: FONT.xs, color: COLORS.textDisabled }}>{isToday ? 'Сегодня' : depDate}</div>
        </div>
      </div>

      {(onRequest || showCancel) && (
        <div style={{ display: 'flex', gap: SPACING.xs }}>
          {onRequest && (
            <button onClick={onRequest} disabled={ride.seatsFree === 0} style={{
              flex: 1, padding: '10px', backgroundColor: ride.seatsFree > 0 ? COLORS.accent : COLORS.bgElevated,
              color: ride.seatsFree > 0 ? '#fff' : COLORS.textDisabled, border: 'none', borderRadius: RADIUS.md,
              fontSize: FONT.sm, fontWeight: 600, cursor: ride.seatsFree > 0 ? 'pointer' : 'default',
            }}>
              {ride.seatsFree > 0 ? 'Запросить место' : 'Мест нет'}
            </button>
          )}
          {showCancel && onCancel && (
            <button onClick={onCancel} style={{
              padding: '10px 16px', backgroundColor: 'transparent', color: COLORS.error,
              border: `1px solid ${COLORS.error}`, borderRadius: RADIUS.md, fontSize: FONT.sm, cursor: 'pointer',
            }}>
              Отменить
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const STATUS_LABEL: Record<string, string> = { active: 'Активна', done: 'Завершена', cancelled: 'Отменена' }
const STATUS_COLOR: Record<string, string> = { active: COLORS.success, done: COLORS.textSecond, cancelled: COLORS.error }

export function RideStatusBadge({ status }: { status: string }) {
  return (
    <span style={{
      fontSize: FONT.xs, color: STATUS_COLOR[status] ?? COLORS.textSecond,
      backgroundColor: (STATUS_COLOR[status] ?? COLORS.textSecond) + '22',
      borderRadius: RADIUS.full, padding: '2px 8px', fontWeight: 600,
    }}>
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}
